import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ROSMessage, ROSTopic, ROSService, ROSNode, ROSParameter, TFFrame } from 'src/types/ros';

interface ROSState {
    connected: boolean;
    connecting: boolean;
    error: string | null;
    topics: ROSTopic[];
    services: ROSService[];
    nodes: ROSNode[];
    parameters: ROSParameter[];
    transforms: TFFrame[];
    messages: Record<string, any[]>;
    subscriptions: Set<string>;
}

const initialState: ROSState = {
    connected: false,
    connecting: false,
    error: null,
    topics: [],
    services: [],
    nodes: [],
    parameters: [],
    transforms: [],
    messages: {},
    subscriptions: new Set<string>(),
};

const rosSlice = createSlice({
    name: 'ros',
    initialState,
    reducers: {
        setConnecting: (state, action: PayloadAction<boolean>) => {
            state.connecting = action.payload;
        },
        setConnected: (state, action: PayloadAction<boolean>) => {
            state.connected = action.payload;
            if (action.payload) {
                state.error = null;
                state.connecting = false;
            }
        },
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.connected = false;
            state.connecting = false;
        },
        setTopics: (state, action: PayloadAction<ROSTopic[]>) => {
            state.topics = action.payload;
        },
        setServices: (state, action: PayloadAction<ROSService[]>) => {
            state.services = action.payload;
        },
        setNodes: (state, action: PayloadAction<ROSNode[]>) => {
            state.nodes = action.payload;
        },
        setParameters: (state, action: PayloadAction<ROSParameter[]>) => {
            state.parameters = action.payload;
        },
        updateParameter: (state, action: PayloadAction<ROSParameter>) => {
            const index = state.parameters.findIndex(p => p.name === action.payload.name);
            if (index !== -1) {
                state.parameters[index] = action.payload;
            } else {
                state.parameters.push(action.payload);
            }
        },
        addSubscription: (state, action: PayloadAction<string>) => {
            state.subscriptions.add(action.payload);
        },
        removeSubscription: (state, action: PayloadAction<string>) => {
            state.subscriptions.delete(action.payload);
        },
        messageReceived: (state, action: PayloadAction<{ topic: string; message: any }>) => {
            const { topic, message } = action.payload;
            if (!state.messages[topic]) {
                state.messages[topic] = [];
            }
            state.messages[topic].push({
                ...message,
                timestamp: Date.now(),
            });
            // Keep only last 100 messages per topic
            if (state.messages[topic].length > 100) {
                state.messages[topic] = state.messages[topic].slice(-100);
            }
        },
        updateTransforms: (state, action: PayloadAction<TFFrame[]>) => {
            state.transforms = action.payload;
        },
        clearMessages: (state, action: PayloadAction<string>) => {
            if (state.messages[action.payload]) {
                state.messages[action.payload] = [];
            }
        },
        reset: (state) => {
            return initialState;
        },
    },
});

export const {
    setConnecting,
    setConnected,
    setError,
    setTopics,
    setServices,
    setNodes,
    setParameters,
    updateParameter,
    addSubscription,
    removeSubscription,
    messageReceived,
    updateTransforms,
    clearMessages,
    reset,
} = rosSlice.actions;

export default rosSlice.reducer;